import { Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { DataManagerService } from '../../../../../data/services/DataManager/data-manager.service';
import { AppConstants } from '../../../../../shared/constants/app-constants';
import { TopbarSearchResult } from '../../topbar-search.model';
import { SearchProvider } from '../record-search.model';
import { formatUsd, joinParts, RECORDS_PER_GROUP, REDSHIFT_PARAMS, REDSHIFT_TIMEOUT_MS } from './list-search';

/** Batch Transactions' endpoint (DepositReportComponent). */
export const BATCH_REPORTS_SERVICE_NAME = '/batch/reports';
/** How far back the search looks; /batch/reports only answers for a date range. */
export const BATCH_SEARCH_DAYS = 90;

/**
 * Batch reference numbers as they are in the data:
 *  - TSYS (ddf_detail.Batch_Reference_Number): 11 digits;
 *  - Fiserv payfac and Fiserv ISO (batch_no): 12 digits, text, and some start with 0
 *    (e.g. 002762130001), so the term is always sent and compared as typed, never as a number;
 *  - PayPal: YYYYMMDD of the day, but PayPal merchants have no Batch Transactions page (see visible).
 * 7 digits is the backend's lower bound (BatchNumberSearchTrait). 13+ digits never get here: the
 * PCI guard drops card-number-shaped queries before any provider runs.
 */
const BATCH_NUMBER = /^\d{7,12}$/;
/** Transaction count as the list shows it (2,000). */
const COUNT = new Intl.NumberFormat('en-US');

/**
 * Batches — GET /batch/reports?searchTerm=<batch number>, the request Batch Transactions sends,
 * over the last BATCH_SEARCH_DAYS days, searched in Redshift (src=R; Fiserv ISO and PayPal read
 * MySQL whatever src says). The backend matches the number exactly for TSYS, Fiserv payfac and ISO.
 * Only digit-only queries are sent; every other query would only match card digits, which this
 * group must not offer (PCI).
 * Opens /payment/batch-detail?reference_number=&date=, as the batch number link on the list does.
 */
export function batchesProvider(dataManager: DataManagerService, now: () => Date = () => new Date()): SearchProvider {
  return {
    group: 'Batches',
    parent: 'payment',
    // Same rule as the Batch Transactions tab (NAV_TABS / PaymentTabsComponent).
    visible: ctx => !ctx.paypalAccount,
    accepts: query => BATCH_NUMBER.test(query),
    search: query => batchReport(dataManager, query, now()).pipe(
      map(rows => toResults(rows, query))
    )
  };
}

function batchReport(dataManager: DataManagerService, batch: string, today: Date): Observable<any[]> {
  const from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - BATCH_SEARCH_DAYS);
  const params: Record<string, string> = {
    searchTerm: batch,
    from_date: isoDate(from),
    to_date: isoDate(today),
    ...REDSHIFT_PARAMS
  };
  const url = AppConstants.BASE_URL + BATCH_REPORTS_SERVICE_NAME + '?'
    + Object.keys(params).map(key => key + '=' + encodeURIComponent(params[key])).join('&');
  return dataManager.getRequest(url, 0).pipe(
    timeout(REDSHIFT_TIMEOUT_MS),
    map(res => reportRows(res?.data)),
    catchError(() => of([]))
  );
}

/** The report is { '<date>': { row_data: [...], row_totals }, grand_total }; flattened to rows. */
function reportRows(data: any): any[] {
  if (!data || typeof data !== 'object') {
    return [];
  }
  return Object.keys(data)
    .filter(key => key !== 'grand_total')
    .reduce((rows, key) => rows.concat(Array.isArray(data[key]?.row_data) ? data[key].row_data : []), [] as any[]);
}

/**
 * Only rows of that very batch (the same search also LIKE-matches card digits, which must not
 * surface here). One result per batch and date: the accepted row, or the rejected one when that
 * is all there is - both open the same batch detail.
 */
function toResults(rows: any[], batch: string): TopbarSearchResult[] {
  const byBatch = new Map<string, any>();
  rows.filter(row => row && String(row.Batch_Reference_Number ?? '') === batch && row.Settlement_Date)
    .forEach(row => {
      const key = row.Settlement_Date;
      const kept = byBatch.get(key);
      if (!kept || (isRejected(kept) && !isRejected(row))) {
        byBatch.set(key, row);
      }
    });
  return Array.from(byBatch.values()).slice(0, RECORDS_PER_GROUP).map(row => toResult(row, batch));
}

function toResult(row: any, batch: string): TopbarSearchResult {
  const rejected = isRejected(row);
  // Amounts as the list shows them: net, in cents; a rejected row counts nothing.
  const amount = rejected ? '' : formatUsd(Number(row.total_net_amt) / 100);
  const count = Number(row.Transactions) || 0;
  return {
    group: 'Batches',
    label: joinParts('Batch ' + batch, amount),
    sublabel: joinParts(row.Settlement_Date, COUNT.format(count) + (count === 1 ? ' transaction' : ' transactions'),
      rejected ? 'Rejected' : ''),
    route: ['/payment/batch-detail'],
    queryParams: { reference_number: batch, date: row.Settlement_Date }
  };
}

function isRejected(row: any): boolean {
  return row.reject_record !== null && row.reject_record !== undefined;
}

/** Local date as yyyy-MM-dd, the format Batch Transactions sends. */
function isoDate(date: Date): string {
  const pad = (n: number) => (n < 10 ? '0' : '') + n;
  return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
}
